import * as Schema from "effect/Schema";

import {
  GitBranch,
  GitHostingProvider,
  GitPullInput,
  type GitCheckoutInput,
  type GitCheckoutResult,
  type GitCreateBranchInput,
  type GitCreateBranchResult,
  type GitCreateWorktreeInput,
  type GitCreateWorktreeResult,
  type GitInitInput,
  type GitListBranchesInput,
  type GitListBranchesResult,
  type GitPullResult,
  type GitStatusLocalResult,
  type GitStatusRemoteResult,
  type GitStatusResult,
  type GitStatusStreamEvent,
} from "./git";
import { NonNegativeInt, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas";

// git.ts keeps both of these module-private; mirrored rather than exported from
// there so this compat layer cannot silently widen git.ts's own surface.
const TrimmedNonEmptyStringSchema = TrimmedNonEmptyString;
const GIT_LIST_BRANCHES_MAX_LIMIT = 200;

/**
 * The `vcs.*` vocabulary t3code's client speaks, expressed over Forge's `git.*`
 * services.
 *
 * Forge and upstream model the same domain and diverged only in naming: upstream
 * generalised "branch" to "ref" and "origin" to "primary remote" for non-Git
 * backends it does not yet have. The element types are identical field for field —
 * VcsRef and GitBranch have the same six fields with the same types — so this is a
 * vocabulary translation at the wrapper level, not a data conversion.
 *
 * It lives in contracts rather than in the server handler because the schemas have
 * to be declared here anyway to join the RPC group, and splitting the shape from
 * the mapping would let them drift.
 */

/** Identical to GitBranch; re-exported under upstream's name so the schema reads honestly. */
export const VcsRef = GitBranch;
export type VcsRef = typeof VcsRef.Type;

export const VcsListRefsInput = Schema.Struct({
  cwd: TrimmedNonEmptyStringSchema,
  query: Schema.optional(TrimmedNonEmptyStringSchema.check(Schema.isMaxLength(256))),
  cursor: Schema.optional(NonNegativeInt),
  // Accepted and ignored: Forge's git service always lists local refs with their
  // matching remotes and has no refresh switch. Rejecting them would fail requests
  // upstream's client legitimately sends, and silently dropping them is safe
  // because the response is a superset of every filter they select.
  includeMatchingRemoteRefs: Schema.optional(Schema.Boolean),
  refKind: Schema.optional(Schema.Literals(["all", "local", "remote"])),
  refresh: Schema.optional(Schema.Boolean),
  limit: Schema.optional(
    PositiveInt.check(Schema.isLessThanOrEqualTo(GIT_LIST_BRANCHES_MAX_LIMIT)),
  ),
});
export type VcsListRefsInput = typeof VcsListRefsInput.Type;

export const VcsListRefsResult = Schema.Struct({
  refs: Schema.Array(VcsRef),
  isRepo: Schema.Boolean,
  hasPrimaryRemote: Schema.Boolean,
  nextCursor: NonNegativeInt.pipe(Schema.NullOr),
  totalCount: NonNegativeInt,
});
export type VcsListRefsResult = typeof VcsListRefsResult.Type;

/** vcs.listRefs request -> git.listBranches request. */
export function toGitListBranchesInput(input: VcsListRefsInput): GitListBranchesInput {
  // Named destructuring rather than a spread: the three upstream-only fields must
  // be dropped explicitly, and listing them here is what makes it obvious that
  // Forge ignores them rather than that someone forgot to map them.
  const { includeMatchingRemoteRefs: _remotes, refKind: _kind, refresh: _refresh, ...rest } = input;
  return rest;
}

/** git.listBranches response -> vcs.listRefs response. */
export function toVcsListRefsResult(result: GitListBranchesResult): VcsListRefsResult {
  return {
    refs: result.branches,
    isRepo: result.isRepo,
    hasPrimaryRemote: result.hasOriginRemote,
    nextCursor: result.nextCursor,
    totalCount: result.totalCount,
  };
}

// ── The rest of the vcs family ──────────────────────────────────────────────
//
// The vocabulary is consistent throughout: refName <-> branch, upstreamRef <->
// upstreamBranch, newRefName <-> newBranch, switchRef <-> checkout. Two upstream
// fields have no Forge equivalent and are REFUSED rather than dropped, because
// unlike the listRefs filters they change what the operation does.

export const VcsPullInput = GitPullInput;
export type VcsPullInput = typeof VcsPullInput.Type;

export const VcsPullResult = Schema.Struct({
  status: Schema.Literals(["pulled", "skipped_up_to_date"]),
  refName: TrimmedNonEmptyStringSchema,
  upstreamRef: TrimmedNonEmptyStringSchema.pipe(Schema.NullOr),
});
export type VcsPullResult = typeof VcsPullResult.Type;

export function toVcsPullResult(result: GitPullResult): VcsPullResult {
  return { status: result.status, refName: result.branch, upstreamRef: result.upstreamBranch };
}

export const VcsCreateWorktreeInput = Schema.Struct({
  cwd: TrimmedNonEmptyStringSchema,
  refName: TrimmedNonEmptyStringSchema,
  newRefName: Schema.optional(TrimmedNonEmptyStringSchema),
  baseRefName: Schema.optional(TrimmedNonEmptyStringSchema),
  path: Schema.NullOr(TrimmedNonEmptyStringSchema),
});
export type VcsCreateWorktreeInput = typeof VcsCreateWorktreeInput.Type;

/**
 * Nested rename the wrapper-level comparison misses: GitWorktree carries `branch`
 * where VcsWorktree carries `refName`. Returning Forge's shape here would hand the
 * client a worktree whose ref name is simply absent.
 */
export const VcsWorktree = Schema.Struct({
  path: TrimmedNonEmptyStringSchema,
  refName: TrimmedNonEmptyStringSchema,
});

export const VcsCreateWorktreeResult = Schema.Struct({ worktree: VcsWorktree });
export type VcsCreateWorktreeResult = typeof VcsCreateWorktreeResult.Type;

export function toVcsCreateWorktreeResult(
  result: GitCreateWorktreeResult,
): VcsCreateWorktreeResult {
  return { worktree: { path: result.worktree.path, refName: result.worktree.branch } };
}

export const VcsCreateRefInput = Schema.Struct({
  cwd: TrimmedNonEmptyStringSchema,
  refName: TrimmedNonEmptyStringSchema,
  switchRef: Schema.optional(Schema.Boolean),
});
export type VcsCreateRefInput = typeof VcsCreateRefInput.Type;

export const VcsCreateRefResult = Schema.Struct({ refName: TrimmedNonEmptyStringSchema });
export type VcsCreateRefResult = typeof VcsCreateRefResult.Type;

export const VcsSwitchRefInput = Schema.Struct({
  cwd: TrimmedNonEmptyStringSchema,
  refName: TrimmedNonEmptyStringSchema,
});
export type VcsSwitchRefInput = typeof VcsSwitchRefInput.Type;

export const VcsSwitchRefResult = Schema.Struct({
  refName: TrimmedNonEmptyStringSchema.pipe(Schema.NullOr),
});
export type VcsSwitchRefResult = typeof VcsSwitchRefResult.Type;

export const VcsInitInput = Schema.Struct({
  cwd: TrimmedNonEmptyStringSchema,
  kind: Schema.optional(Schema.Literals(["git", "jj", "unknown"])),
});
export type VcsInitInput = typeof VcsInitInput.Type;

/**
 * Raised when a request carries something Forge cannot honour. Returned instead of
 * proceeding, because both cases would otherwise change the result silently: a
 * worktree created from the wrong base commit, or a Git repository initialised
 * where the caller asked for a Jujutsu one.
 */
export class VcsUnsupportedRequestError extends Schema.ErrorClass<VcsUnsupportedRequestError>(
  "@cmmd-forge/contracts/VcsUnsupportedRequestError",
)({
  _tag: Schema.tag("VcsUnsupportedRequestError"),
  message: Schema.String,
}) {}

export function toGitCreateWorktreeInput(
  input: VcsCreateWorktreeInput,
): GitCreateWorktreeInput | VcsUnsupportedRequestError {
  if (input.baseRefName !== undefined) {
    return new VcsUnsupportedRequestError({
      message:
        "Forge cannot create a worktree from an explicit base ref; omit baseRefName to branch from the named ref.",
    });
  }
  const { refName, newRefName, baseRefName: _base, ...rest } = input;
  return {
    ...rest,
    branch: refName,
    ...(newRefName === undefined ? {} : { newBranch: newRefName }),
  };
}

export function toGitInitInput(input: VcsInitInput): GitInitInput | VcsUnsupportedRequestError {
  if (input.kind !== undefined && input.kind !== "git") {
    return new VcsUnsupportedRequestError({
      message: `Forge only supports Git repositories; "${input.kind}" is not available.`,
    });
  }
  return { cwd: input.cwd };
}

export function toGitCreateBranchInput(input: VcsCreateRefInput): GitCreateBranchInput {
  const { refName, switchRef, ...rest } = input;
  return { ...rest, branch: refName, ...(switchRef === undefined ? {} : { checkout: switchRef }) };
}

export function toVcsCreateRefResult(result: GitCreateBranchResult): VcsCreateRefResult {
  return { refName: result.branch };
}

export function toGitCheckoutInput(input: VcsSwitchRefInput): GitCheckoutInput {
  return { cwd: input.cwd, branch: input.refName };
}

export function toVcsSwitchRefResult(result: GitCheckoutResult): VcsSwitchRefResult {
  return { refName: result.branch };
}

// ── Status ──────────────────────────────────────────────────────────────────
//
// The one part of the family that is not a clean rename. Four renamed fields, one
// upstream field Forge cannot supply, and two Forge fields upstream does not model.
// The nested shapes matter as much as the top level: workingTree is identical, but
// the change-request struct renames two more fields, and the provider struct is
// identical inside despite both sides naming the type differently. The provider
// kind and change-request state enums were verified to carry exactly the same
// literals.

export const VcsStatusChangeRequest = Schema.Struct({
  number: PositiveInt,
  title: TrimmedNonEmptyStringSchema,
  url: Schema.String,
  baseRef: TrimmedNonEmptyStringSchema,
  headRef: TrimmedNonEmptyStringSchema,
  state: Schema.Literals(["open", "closed", "merged"]),
  updatedAt: Schema.optional(Schema.NullOr(Schema.String)),
});

const VcsStatusLocalShape = {
  isRepo: Schema.Boolean,
  sourceControlProvider: Schema.optional(GitHostingProvider),
  hasPrimaryRemote: Schema.Boolean,
  isDefaultRef: Schema.Boolean,
  refName: Schema.NullOr(TrimmedNonEmptyStringSchema),
  hasWorkingTreeChanges: Schema.Boolean,
  // Inlined rather than imported: git.ts declares this shape inside
  // GitStatusLocalShape and exports no name for it. Verified identical to
  // upstream's, field for field.
  workingTree: Schema.Struct({
    files: Schema.Array(
      Schema.Struct({
        path: TrimmedNonEmptyStringSchema,
        insertions: NonNegativeInt,
        deletions: NonNegativeInt,
      }),
    ),
    insertions: NonNegativeInt,
    deletions: NonNegativeInt,
  }),
} as const;

const VcsStatusRemoteShape = {
  hasUpstream: Schema.Boolean,
  aheadCount: NonNegativeInt,
  behindCount: NonNegativeInt,
  aheadOfDefaultCount: Schema.optional(NonNegativeInt),
  pr: Schema.NullOr(VcsStatusChangeRequest),
} as const;

export const VcsStatusLocal = Schema.Struct(VcsStatusLocalShape);
export type VcsStatusLocal = typeof VcsStatusLocal.Type;

export const VcsStatusRemote = Schema.Struct(VcsStatusRemoteShape);
export type VcsStatusRemote = typeof VcsStatusRemote.Type;

export const VcsStatusResult = Schema.Struct({
  ...VcsStatusLocalShape,
  ...VcsStatusRemoteShape,
});
export type VcsStatusResult = typeof VcsStatusResult.Type;

export const VcsStatusStreamEvent = Schema.Union([
  Schema.TaggedStruct("snapshot", {
    local: VcsStatusLocal,
    remote: Schema.NullOr(VcsStatusRemote),
  }),
  Schema.TaggedStruct("localUpdated", { local: VcsStatusLocal }),
  Schema.TaggedStruct("remoteUpdated", { remote: Schema.NullOr(VcsStatusRemote) }),
]);
export type VcsStatusStreamEvent = typeof VcsStatusStreamEvent.Type;

export function toVcsStatusLocal(status: GitStatusLocalResult): VcsStatusLocal {
  return {
    isRepo: status.isRepo,
    // Same struct inside — kind/name/baseUrl with matching enums — so this is a
    // key rename, not a conversion.
    ...(status.hostingProvider === undefined
      ? {}
      : { sourceControlProvider: status.hostingProvider }),
    hasPrimaryRemote: status.hasOriginRemote,
    isDefaultRef: status.isDefaultBranch,
    refName: status.branch,
    hasWorkingTreeChanges: status.hasWorkingTreeChanges,
    workingTree: status.workingTree,
  };
}

export function toVcsStatusRemote(remote: GitStatusRemoteResult): VcsStatusRemote {
  return {
    hasUpstream: remote.hasUpstream,
    aheadCount: remote.aheadCount,
    behindCount: remote.behindCount,
    // aheadOfDefaultCount is omitted, not zeroed: Forge does not compute it, and
    // reporting 0 would assert the branch is level with the default when that is
    // simply unknown. It is optional upstream and unread by the client.
    pr:
      remote.pr === null
        ? null
        : {
            number: remote.pr.number,
            title: remote.pr.title,
            url: remote.pr.url,
            baseRef: remote.pr.baseBranch,
            headRef: remote.pr.headBranch,
            state: remote.pr.state,
          },
    // Dropped: defaultBranch and diffVsBase are Forge-only and have nowhere to go.
  };
}

/** The flat shape vcs.refreshStatus answers with. */
export function toVcsStatusResult(status: GitStatusResult): VcsStatusResult {
  return { ...toVcsStatusLocal(status), ...toVcsStatusRemote(status) };
}

/**
 * The stream union is structurally identical on both sides — same three tags, same
 * `local`/`remote` keys — so only the inner results need mapping.
 */
export function toVcsStatusStreamEvent(event: GitStatusStreamEvent): VcsStatusStreamEvent {
  switch (event._tag) {
    case "snapshot":
      return {
        _tag: "snapshot",
        local: toVcsStatusLocal(event.local),
        remote: event.remote === null ? null : toVcsStatusRemote(event.remote),
      };
    case "localUpdated":
      return { _tag: "localUpdated", local: toVcsStatusLocal(event.local) };
    case "remoteUpdated":
      return {
        _tag: "remoteUpdated",
        remote: event.remote === null ? null : toVcsStatusRemote(event.remote),
      };
  }
}
